import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable Card component with glassmorphism design
 * Supports multiple variants, hover effects, and structured content slots
 */
const Card = forwardRef(({
  children,
  variant = 'default',
  hover = false,
  className = '',
  header,
  footer,
  ...props
}, ref) => {

  // Base styles with glassmorphism
  const baseStyles = 'rounded-xl transition-all duration-300';

  // Variant styles
  const variants = {
    default: 'glass',
    elevated: 'glass shadow-xl shadow-black/20',
    gradient: 'glass bg-gradient-to-br from-primary-900/20 via-black/20 to-secondary-900/20',
    solid: 'glass',
    outline: 'bg-transparent border border-white/20',
  };

  // Hover effect styles
  const hoverStyles = hover
    ? 'hover:border-white/30 hover:bg-black/30 hover:shadow-lg hover:shadow-primary-500/10 hover:-translate-y-1 cursor-pointer'
    : '';

  // Combine all styles
  const cardClasses = `${baseStyles} ${variants[variant]} ${hoverStyles} ${className}`;

  return (
    <div
      ref={ref}
      className={cardClasses}
      {...props}
    >
      {header && (
        <CardHeader>
          {header}
        </CardHeader>
      )}

      <CardContent hasHeader={!!header} hasFooter={!!footer}>
        {children}
      </CardContent>

      {footer && (
        <CardFooter>
          {footer}
        </CardFooter>
      )}
    </div>
  );
});

Card.displayName = 'Card';

Card.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'elevated', 'gradient', 'solid', 'outline']),
  hover: PropTypes.bool,
  className: PropTypes.string,
  header: PropTypes.node,
  footer: PropTypes.node,
};

/**
 * CardHeader component for card titles and actions
 */
export const CardHeader = ({ children, className = '' }) => {
  return (
    <div className={`px-6 py-4 border-b border-white/10 ${className}`}>
      {children}
    </div>
  );
};

CardHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

/**
 * CardContent component for main card body
 */
export const CardContent = ({ children, className = '', hasHeader = false, hasFooter = false }) => {
  // Adjust padding based on presence of header/footer
  const paddingClass = hasHeader || hasFooter ? 'p-6' : 'p-6';

  return (
    <div className={`${paddingClass} ${className}`}>
      {children}
    </div>
  );
};

CardContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  hasHeader: PropTypes.bool,
  hasFooter: PropTypes.bool,
};

/**
 * CardFooter component for actions and metadata
 */
export const CardFooter = ({ children, className = '' }) => {
  return (
    <div className={`px-6 py-4 border-t border-white/10 ${className}`}>
      {children}
    </div>
  );
};

CardFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

/**
 * CardTitle component for consistent header styling
 */
export const CardTitle = ({ children, className = '', as: Component = 'h3' }) => {
  return (
    <Component className={`text-xl font-semibold text-white ${className}`}>
      {children}
    </Component>
  );
};

CardTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  as: PropTypes.string,
};

/**
 * CardDescription component for subtitle text
 */
export const CardDescription = ({ children, className = '' }) => {
  return (
    <p className={`text-sm text-gray-400 mt-1 ${className}`}>
      {children}
    </p>
  );
};

CardDescription.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default Card;
